import {
  AddressAutocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  fetchAddressDetail,
  fetchAddressSuggestions,
  newSessionToken,
  placesProvider,
  type ActiveProvider,
} from '../../lib/places-api';
import type {
  ParcelInputDto,
  ComplainantRelationship,
  FilePriority,
  IntakeMethod,
  OwnerType,
  PriorityReason,
  PropertyType,
} from '@munifocus/contracts-gov';
import {
  taxonomyLabel,
  PARCEL_DETAIL_KEYS,
  PID_PATTERN,
  COMPLAINANT_RELATIONSHIPS,
  FILE_PRIORITIES,
  NB_COUNTIES,
  NB_COUNTY_KEY,
  OWNER_TYPES,
  PRIORITY_REASONS,
} from '@munifocus/contracts-gov';
import { needsSpecify, SpecifyField } from '../SpecifyField';
import { useIntakeMethods, usePropertyTypes } from '../../lib/taxonomies-api';
import { LocalGovernmentPicker } from './LocalGovernmentPicker';
import { OfficerPicker } from './OfficerPicker';
import type { FileForm, FileFormGateField } from './useFileForm';
import type { SubmitValidation } from '../../lib/useSubmitValidation';
import { EnforcedLawPicker } from './EnforcedLawPicker';
import { BylawCategoryPicker } from './BylawCategoryPicker';

/** Whether a parcel row's detail fields are worth the space they take. */
function parcelDetailShown(row: ParcelInputDto, expanded: boolean): boolean {
  return expanded || PARCEL_DETAIL_KEYS.some((key) => (row[key] ?? '') !== '');
}

/**
 * Every field of a file, in the order the officer is asked for them.
 *
 * Rendered by both the create screen and the edit screen. That is the whole
 * point: "new" and "edit" are the same record seen at two moments, and a
 * product that asks for one set of things when opening a file and a different,
 * smaller set when fixing it has simply lost track of what a file is.
 *
 * `snb` is the only difference between the two callers — the record-upload
 * panel, which stages uploads before the file exists and attaches them to it
 * afterwards. The caller owns that because only the caller knows which.
 */
/** The occupant boxes, as one list so the disclosure and the payload agree. */
const OCCUPANT_FIELDS = [
  'occupantName',
  'occupantPhone',
  'occupantAddress',
  'occupantCity',
  'occupantProvince',
  'occupantPostalCode',
] as const;

export function FileFormFields({
  form,
  snb,
  complaintLocked = false,
  autoSelectSoleLocalGovernment = false,
  validation,
}: {
  form: FileForm;
  /**
   * The submit-time gate from `useSubmitValidation(fileFormGateMessages(...))`.
   * Optional so a read-only or partial use of the fields needs no validator.
   */
  validation?: SubmitValidation<FileFormGateField>;
  snb?: ReactNode;
  /**
   * Pre-select the local government when the officer holds exactly one
   * appointment. Set on intake only — see `LocalGovernmentPicker`.
   */
  autoSelectSoleLocalGovernment?: boolean;
  /**
   * Freeze what was reported.
   *
   * Set once the file has left intake: the complaint is the record of first
   * contact, and downstream work — eventually a served notice — rests on it.
   * Shown rather than hidden, because an officer still needs to read what was
   * reported while correcting the property around it.
   */
  complaintLocked?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';

  // Which provider is answering decides the attribution, which is a licence
  // requirement on both of them rather than a nicety.
  const [addressProvider, setAddressProvider] = useState<ActiveProvider>(null);
  useEffect(() => {
    let active = true;
    placesProvider()
      .then((id) => active && setAddressProvider(id))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const intakeMethods = useIntakeMethods();
  const propertyTypes = usePropertyTypes();
  const { values, set } = form;
  const errors = validation?.errors ?? {};
  const register = (key: FileFormGateField) => validation?.register(key);
  // Focus target for the PID message: the first row that is not a valid PID.
  const firstBadPid = form.values.parcelRows.findIndex(
    (r) => !!r.pid.trim() && !PID_PATTERN.test(r.pid.trim()),
  );
  // Rows the officer opened by hand. A row that already carries detail shows it
  // regardless, so an uploaded record never hides behind a disclosure.
  const [expandedParcels, setExpandedParcels] = useState<Set<number>>(new Set());
  // Same rule for the occupant block: opened by hand, or forced open because the
  // record already names one. Derived rather than seeded into state, because on
  // the edit screen the file's values arrive after the first render — a
  // `useState` initialiser would read them as empty and collapse a block that
  // holds data.
  const [occupantOpen, setOccupantOpen] = useState(false);
  const hasOccupant = OCCUPANT_FIELDS.some((key) => values[key].trim() !== '');
  const showOccupant = occupantOpen || hasOccupant;

  return (
    <Stack spacing={2}>
      {/* No default selection. The method decides which reporter details are
          collected, so pre-selecting one answers that on the officer's behalf. */}
      <FormControl
        fullWidth
        size="small"
        required
        disabled={complaintLocked}
        error={Boolean(errors.intakeMethod)}
      >
        <InputLabel>{t('gov.file.intakeMethod')}</InputLabel>
        <Select
          label={t('gov.file.intakeMethod')}
          aria-label={t('gov.file.intakeMethod')}
          inputRef={register('intakeMethod')}
          value={values.intakeMethod}
          disabled={complaintLocked}
          onChange={(e) => set('intakeMethod', e.target.value as IntakeMethod)}
        >
          {(intakeMethods.data ?? []).map((m) => (
            <MenuItem key={m.key} value={m.key}>
              {taxonomyLabel(m, i18n.language)}
            </MenuItem>
          ))}
        </Select>
        {errors.intakeMethod && <FormHelperText>{errors.intakeMethod}</FormHelperText>}
      </FormControl>
      <SpecifyField
        code={values.intakeMethod}
        value={values.intakeMethodOther}
        onChange={(v) => set('intakeMethodOther', v)}
        disabled={complaintLocked}
      />

      {/* Flagged for coordinator reporting per the §A programmer note. Derived
          from the method rather than stored, so it can't drift out of step. */}
      {form.councilReferral && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.intake.councilReferralFlag')}
        </Alert>
      )}

      {/* The batch path exists now, so this points at it rather than
          apologising. An officer who picked this method on the single-file form
          almost certainly has a spreadsheet in front of them. */}
      {values.intakeMethod === 'bulk_import' && (
        <Alert
          severity="info"
          sx={{ py: 0 }}
          action={
            <Button component={RouterLink} to="/files/import" size="small" variant="text">
              {t('gov.intake.bulkImportOpen')}
            </Button>
          }
        >
          {t('gov.intake.bulkImportAvailable')}
        </Alert>
      )}

      {form.showComplainant && (
        <>
          <Divider textAlign="left" sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t('gov.intake.complainant.title')}
            </Typography>
          </Divider>

          {/* Contact details are still captured and stored when anonymous —
              the spec retains them internally for follow-up. The flag governs
              what may leave the file, so the fields stay enabled. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={values.anonymous}
                disabled={complaintLocked}
                onChange={(e) => set('anonymous', e.target.checked)}
              />
            }
            label={t('gov.file.anonymous')}
          />
          {values.anonymous && (
            <Alert severity="info" sx={{ py: 0 }}>
              {t('gov.intake.anonymousHint')}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.complainantName')}
            disabled={complaintLocked}
            value={values.complainantName}
            onChange={(e) => set('complainantName', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            type="email"
            label={t('gov.file.complainantEmail')}
            disabled={complaintLocked}
            helperText={t('gov.file.complainantEmailHint')}
            value={values.complainantEmail}
            onChange={(e) => set('complainantEmail', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            type="tel"
            label={t('gov.file.complainantPhone')}
            disabled={complaintLocked}
            value={values.complainantPhone}
            onChange={(e) => set('complainantPhone', e.target.value)}
          />

          {/* The complainant's own address — four boxes because the complaint
              report prints four, and splitting one line back apart at
              generation time would only be guesswork. Not the property, which
              is the `address` field above. */}
          <TextField
            fullWidth
            size="small"
            label={t('gov.file.complainantAddress')}
            disabled={complaintLocked}
            value={values.complainantAddress}
            onChange={(e) => set('complainantAddress', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.complainantCity')}
            disabled={complaintLocked}
            value={values.complainantCity}
            onChange={(e) => set('complainantCity', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.complainantProvince')}
            disabled={complaintLocked}
            value={values.complainantProvince}
            onChange={(e) => set('complainantProvince', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.complainantPostalCode')}
            disabled={complaintLocked}
            value={values.complainantPostalCode}
            onChange={(e) => set('complainantPostalCode', e.target.value)}
          />

          {/* Context only — the spec is explicit that it is not a determinant
              of complaint validity, so nothing downstream gates on it. */}
          <FormControl fullWidth size="small" disabled={complaintLocked}>
            <InputLabel>{t('gov.file.complainantRelationship')}</InputLabel>
            <Select
              label={t('gov.file.complainantRelationship')}
              aria-label={t('gov.file.complainantRelationship')}
              value={values.complainantRelationship}
              onChange={(e) =>
                set('complainantRelationship', e.target.value as ComplainantRelationship)
              }
            >
              {COMPLAINANT_RELATIONSHIPS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.complainantRelationship.${r}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <SpecifyField
            code={values.complainantRelationship}
            value={values.complainantRelationshipOther}
            onChange={(v) => set('complainantRelationshipOther', v)}
            disabled={complaintLocked}
          />

          <FormControl fullWidth size="small" disabled={complaintLocked}>
            <InputLabel>{t('gov.file.complainantLanguage')}</InputLabel>
            <Select
              label={t('gov.file.complainantLanguage')}
              aria-label={t('gov.file.complainantLanguage')}
              value={values.complainantLocale}
              onChange={(e) => set('complainantLocale', e.target.value as 'en' | 'fr')}
            >
              <MenuItem value="en">{t('gov.file.languageEn')}</MenuItem>
              <MenuItem value="fr">{t('gov.file.languageFr')}</MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {form.showInternalSource && (
        <>
          <Divider textAlign="left" sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t('gov.intake.internalSource.title')}
            </Typography>
          </Divider>

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.referringPerson')}
            disabled={complaintLocked}
            helperText={t('gov.file.referringPersonHint')}
            value={values.referringPerson}
            onChange={(e) => set('referringPerson', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('gov.file.internalContext')}
            disabled={complaintLocked}
            helperText={t('gov.file.internalContextHint')}
            value={values.internalContext}
            onChange={(e) => set('internalContext', e.target.value)}
          />
        </>
      )}

      <AddressAutocomplete
        label={t('gov.file.address')}
        required
        error={Boolean(errors.address)}
        helperText={errors.address}
        inputRef={register('address')}
        value={values.address}
        onChange={(v) => set('address', v)}
        attribution={t(`gov.places.attribution.${addressProvider ?? 'osm'}`)}
        newSessionToken={newSessionToken}
        fetchSuggestions={(q, token) => fetchAddressSuggestions(q, token, language)}
        fetchDetail={(placeId, token) => fetchAddressDetail(placeId, token, language)}
        onPick={(place) => {
          // The web form keeps a single address line (there is no separate
          // municipality box here as there is on mobile), so the city is folded
          // in — matching what `composeAddress` sends from the app.
          set('address', place.city ? `${place.address}, ${place.city}` : place.address);
        }}
      />

      {/* Directly under the address, because it answers a question about it:
          whose jurisdiction this property falls in. It is also what decides
          which roster the officer picker below can draw from. */}
      <LocalGovernmentPicker
        form={form}
        autoSelectSole={autoSelectSoleLocalGovernment}
        error={errors.localGovernmentId}
        inputRef={register('localGovernmentId')}
      />

      {/* Downstream of the anchor: the roster it draws from is per local
          government, and so is the authority the server checks the choice
          against. */}
      <OfficerPicker form={form} />

      {/* No default selection. Public-land categories suppress the Entry &
          Access Module at Stage 2, so a pre-selected value would decide the
          officer's entry position for them. */}
      <FormControl fullWidth size="small" required error={Boolean(errors.propertyType)}>
        <InputLabel>{t('gov.file.propertyType')}</InputLabel>
        <Select
          label={t('gov.file.propertyType')}
          aria-label={t('gov.file.propertyType')}
          inputRef={register('propertyType')}
          value={values.propertyType}
          onChange={(e) => set('propertyType', e.target.value as PropertyType)}
        >
          {(propertyTypes.data ?? []).map((pt) => (
            <MenuItem key={pt.key} value={pt.key}>
              {taxonomyLabel(pt, i18n.language)}
            </MenuItem>
          ))}
        </Select>
        {errors.propertyType && <FormHelperText>{errors.propertyType}</FormHelperText>}
      </FormControl>
      <SpecifyField
        code={values.propertyType}
        value={values.propertyTypeOther}
        onChange={(v) => set('propertyTypeOther', v)}
      />

      {/* §F. Routine is a safe default here in a way the intake method was
          not: it asserts no urgency, and the reason below is what any raised
          level has to justify. */}
      <FormControl fullWidth size="small" required>
        <InputLabel>{t('gov.file.priority')}</InputLabel>
        <Select
          label={t('gov.file.priority')}
          aria-label={t('gov.file.priority')}
          value={values.priority}
          onChange={(e) => set('priority', e.target.value as FilePriority)}
        >
          {FILE_PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {t(`gov.priority.${p}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Hidden and not required at routine, per the §F programmer note. */}
      {form.showPriorityReason && (
        <>
          <FormControl fullWidth size="small" required error={Boolean(errors.priorityReason)}>
            <InputLabel>{t('gov.file.priorityReason')}</InputLabel>
            <Select
              label={t('gov.file.priorityReason')}
              aria-label={t('gov.file.priorityReason')}
              inputRef={register('priorityReason')}
              value={values.priorityReason}
              onChange={(e) => set('priorityReason', e.target.value as PriorityReason)}
            >
              {PRIORITY_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.priorityReason.${r}`)}
                </MenuItem>
              ))}
            </Select>
            {errors.priorityReason && <FormHelperText>{errors.priorityReason}</FormHelperText>}
          </FormControl>
          <SpecifyField
            code={values.priorityReason}
            value={values.priorityReasonOther}
            onChange={(v) => set('priorityReasonOther', v)}
          />
        </>
      )}

      <Divider textAlign="left" sx={{ mt: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {t('gov.intake.tombstone.title')}
        </Typography>
      </Divider>
      {/* s.144(1) — property identification carried from the SNB PAN record. */}
      <Typography variant="caption" color="text.secondary">
        {t('gov.intake.tombstone.subtitle')}
      </Typography>

      {snb}

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.pan')}
        value={values.pan}
        onChange={(e) => set('pan', e.target.value)}
        error={Boolean(errors.pan)}
        helperText={errors.pan}
        inputRef={register('pan')}
        slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 12 } }}
      />

      {/* The 15 NB counties. Stored as the printed PAN name, so this is a
          picker over free-text values rather than a coded enum. */}
      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.file.county')}</InputLabel>
        <Select
          label={t('gov.file.county')}
          aria-label={t('gov.file.county')}
          value={values.county}
          onChange={(e) => set('county', e.target.value)}
        >
          {NB_COUNTIES.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`gov.county.${NB_COUNTY_KEY[c]}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* One PAN → many PIDs. Each additional PID is stored against the file. */}
      {values.parcelRows.map((row, idx) => (
        <Box key={idx}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label={
                idx === 0
                  ? t('gov.file.propertyId')
                  : t('gov.intake.tombstone.pidLabel', { n: idx + 1 })
              }
              value={row.pid}
              onChange={(e) => form.setPidAt(idx, e.target.value)}
              error={Boolean(errors.pids) && !!row.pid.trim() && !PID_PATTERN.test(row.pid.trim())}
              helperText={
                errors.pids && !!row.pid.trim() && !PID_PATTERN.test(row.pid.trim())
                  ? errors.pids
                  : undefined
              }
              inputRef={idx === firstBadPid ? register('pids') : undefined}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 8 } }}
            />
            {idx > 0 && (
              <IconButton
                aria-label={t('gov.intake.tombstone.removePid')}
                size="small"
                onClick={() => form.removePid(idx)}
              >
                <Icons.Trash2 size={iconSize.lg} />
              </IconButton>
            )}
          </Stack>

          {/* What the Parcel record says about this PID. Twelve fields nobody
              types by hand: they arrive filled from an uploaded record, and
              until one does they are a wall of empty boxes between the officer
              and the rest of the form. So they show when they hold something,
              and are one click away when they do not. */}
          {parcelDetailShown(row, expandedParcels.has(idx)) ? (
            <Grid
              container
              spacing={1.5}
              // `mt` matches the grid's own row spacing so the gap under the PID
              // reads the same as the gaps between the detail rows.
              sx={{ mt: 1.5, pl: 0.25, pr: idx > 0 ? 5 : 0 }}
            >
              {PARCEL_DETAIL_KEYS.map((key) => (
                <Grid key={key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t(`gov.parcel.${key}`)}
                    value={row[key] ?? ''}
                    onChange={(e) => form.setParcelFieldAt(idx, key, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Button
              size="small"
              startIcon={<Icons.ChevronDown size={iconSize.md} />}
              onClick={() => setExpandedParcels((prev) => new Set(prev).add(idx))}
              sx={{ mt: 0.5 }}
            >
              {t('gov.intake.tombstone.showParcelDetail')}
            </Button>
          )}
        </Box>
      ))}
      <Box>
        <Button size="small" startIcon={<Icons.Plus size={iconSize.md} />} onClick={form.addPid}>
          {t('gov.intake.tombstone.addPid')}
        </Button>
      </Box>

      {/* By-law information sits directly beneath the PID list: the officer
          identifies the parcels first, then records what they are in
          contravention of. One entry covers the file — a PAN and its parcels are
          one property under one by-law, even when a straddled building spans two
          PIDs. */}
      <BylawCategoryPicker form={form} />

      {/* Directly below the category, because the two are read together: the
          category routes the workflow, this names the instrument. */}
      <EnforcedLawPicker form={form} />
      <SpecifyField
        code={values.bylawCategory}
        value={values.bylawCategoryOther}
        onChange={(v) => set('bylawCategoryOther', v)}
        labelKey={'gov.specify.bylaw'}
      />

      {/* The rest of the PAN header grid. These are read off the SNB record and
          carried onto the complaint report and the statutory notices, so they
          need to be visible and correctable — an officer has to be able to stand
          behind every value the documents print. */}
      <TextField
        fullWidth
        size="small"
        label={t('gov.file.propertyDescription')}
        value={values.propertyDescription}
        onChange={(e) => set('propertyDescription', e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.taxingAuthority')}
        value={values.taxingAuthority}
        onChange={(e) => set('taxingAuthority', e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.taxClass')}
        value={values.taxClass}
        onChange={(e) => set('taxClass', e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.neighbourhood')}
        value={values.neighbourhood}
        onChange={(e) => set('neighbourhood', e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.harmonization')}
        value={values.harmonizationStatus}
        onChange={(e) => set('harmonizationStatus', e.target.value)}
      />
      {form.harmonized && values.parcelRows.length < 2 && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.intake.tombstone.multiPidHint')}
        </Alert>
      )}

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.ownerName')}
        value={values.ownerName}
        disabled={values.ownerUnknown}
        onChange={(e) => set('ownerName', e.target.value)}
      />

      <TextField
        fullWidth
        size="small"
        type="email"
        label={t('gov.file.ownerEmail')}
        helperText={t('gov.file.ownerEmailHint')}
        value={values.ownerEmail}
        disabled={values.ownerUnknown}
        onChange={(e) => set('ownerEmail', e.target.value)}
      />

      {/* The PAN record carries no telephone, so this is always officer-entered
          — from the courtesy call or the pre-visit contact. */}
      <TextField
        fullWidth
        size="small"
        type="tel"
        label={t('gov.file.ownerPhone')}
        value={values.ownerPhone}
        disabled={values.ownerUnknown}
        onChange={(e) => set('ownerPhone', e.target.value)}
      />

      <FormControl fullWidth size="small" disabled={values.ownerUnknown}>
        <InputLabel>{t('gov.file.ownerLanguage')}</InputLabel>
        <Select
          label={t('gov.file.ownerLanguage')}
          aria-label={t('gov.file.ownerLanguage')}
          value={values.ownerLocale}
          onChange={(e) => set('ownerLocale', e.target.value as 'en' | 'fr')}
        >
          <MenuItem value="en">{t('gov.file.languageEn')}</MenuItem>
          <MenuItem value="fr">{t('gov.file.languageFr')}</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" disabled={values.ownerUnknown}>
        <InputLabel>{t('gov.file.ownerType')}</InputLabel>
        <Select
          label={t('gov.file.ownerType')}
          aria-label={t('gov.file.ownerType')}
          value={values.ownerType}
          onChange={(e) => set('ownerType', e.target.value as OwnerType)}
        >
          {OWNER_TYPES.map((ot) => (
            <MenuItem key={ot} value={ot}>
              {t(`gov.ownerType.${ot}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <SpecifyField
        code={values.ownerType}
        value={values.ownerTypeOther}
        onChange={(v) => set('ownerTypeOther', v)}
      />

      <TextField
        fullWidth
        size="small"
        label={t('gov.file.mailingAddress')}
        value={values.mailingAddress}
        disabled={values.ownerUnknown}
        onChange={(e) => set('mailingAddress', e.target.value)}
      />

      {/* Its own PAN column, kept out of `mailingAddress` so it stays correctable
          without editing the street address. */}
      <TextField
        fullWidth
        size="small"
        label={t('gov.file.postalCode')}
        value={values.postalCode}
        disabled={values.ownerUnknown}
        onChange={(e) => set('postalCode', e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={values.ownerUnknown}
            onChange={(e) => set('ownerUnknown', e.target.checked)}
          />
        }
        label={t('gov.file.ownerUnknown')}
      />

      <Divider sx={{ mt: 1 }} />

      {/* The occupant, where that is not the owner. s. 131(1) binds premises
          "owned or occupied" and s. 132 allows notice to either, so a tenant is
          a party to the enforcement rather than a note about the owner. Nothing
          here is ever defaulted from the owner.
          
          Behind a disclosure because most properties are owner-occupied: six
          boxes an officer scrolls past on every intake is friction, and the
          form is long already. Forced open whenever the record holds an
          occupant, so an existing one can never be hidden by a collapsed
          section — including on the edit screen, where the values arrive after
          the first render. */}
      <FormControlLabel
        control={
          <Checkbox
            checked={showOccupant}
            disabled={hasOccupant}
            onChange={(e) => setOccupantOpen(e.target.checked)}
          />
        }
        label={t('gov.file.occupantSection')}
      />

      {showOccupant && (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('gov.file.occupantName')}
            helperText={t('gov.file.occupantNameHint')}
            value={values.occupantName}
            onChange={(e) => set('occupantName', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            type="tel"
            label={t('gov.file.occupantPhone')}
            value={values.occupantPhone}
            onChange={(e) => set('occupantPhone', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.occupantAddress')}
            value={values.occupantAddress}
            onChange={(e) => set('occupantAddress', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.occupantCity')}
            value={values.occupantCity}
            onChange={(e) => set('occupantCity', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.occupantProvince')}
            value={values.occupantProvince}
            onChange={(e) => set('occupantProvince', e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label={t('gov.file.occupantPostalCode')}
            value={values.occupantPostalCode}
            onChange={(e) => set('occupantPostalCode', e.target.value)}
          />
        </>
      )}

      <Divider sx={{ mt: 1 }} />

      <TextField
        fullWidth
        multiline
        minRows={5}
        required
        disabled={complaintLocked}
        label={t('gov.file.description')}
        error={Boolean(errors.description)}
        helperText={errors.description}
        inputRef={register('description')}
        value={values.description}
        onChange={(e) => set('description', e.target.value)}
      />
    </Stack>
  );
}

/** Re-exported so callers gating `*Other` payload fields don't import two modules. */
export { needsSpecify };
